import { useMemo, useState } from 'react'
import { useMutation, useQuery } from '@apollo/client'
import { Link } from 'react-router-dom'
import { toast } from 'react-toastify'
import { ALL_COURSES, COURSE_BY_ID } from '../../gql/queries/academyQuery'
import {
  DELETE_COURSE,
  DELETE_LESSON,
  REORDER_LESSONS,
} from '../../gql/mutations/academyMutations'
import {
  IAllCoursesResponse,
  ICourse,
  ICourseByIdResponse,
  IDeleteCourseResponse,
  IDeleteLessonResponse,
  ILesson,
  IReorderLessonsResponse,
} from '../../types/academy'
import { useDisclourse } from '../../hooks/useDisclourse'
import { parseId } from '../../helpers'
import { ModalCourseForm } from './components/ModalCourseForm'
import { ModalLessonForm } from './components/ModalLessonForm'
import { QuizBuilderModal } from './components/QuizBuilderModal'
import { getResourceTypeIcon } from '../resourceCenter/resourceTypeHelpers'
import { useIntl } from 'react-intl'
import {
  ACADEMY_MESSAGES as A,
  BACKEND_ERROR,
  DOT_SEP,
  ORDINAL_DOT,
} from './academyMessages'

export const ManageCoursesPage = () => {
  const intl = useIntl()
  const { onOpen: onOpenCourse, isOpen: isOpenCourse, onClose: onCloseCourse } = useDisclourse()
  const { onOpen: onOpenLesson, isOpen: isOpenLesson, onClose: onCloseLesson } = useDisclourse()
  const { onOpen: onOpenQuiz, isOpen: isOpenQuiz, onClose: onCloseQuiz } = useDisclourse()

  const [editingCourse, setEditingCourse] = useState<ICourse | null>(null)
  const [selectedCourseId, setSelectedCourseId] = useState<string | null>(null)
  const [editingLesson, setEditingLesson] = useState<ILesson | null>(null)
  const [quizLesson, setQuizLesson] = useState<ILesson | null>(null)

  const {
    data: dataCourses,
    loading: loadingCourses,
    error,
    refetch: refetchCourses,
  } = useQuery<IAllCoursesResponse>(ALL_COURSES, {
    fetchPolicy: 'network-only',
  })

  const courses = useMemo(() => dataCourses?.allCourses || [], [dataCourses])

  const {
    data: dataCourse,
    loading: loadingCourse,
    refetch: refetchCourse,
  } = useQuery<ICourseByIdResponse>(COURSE_BY_ID, {
    variables: { id: Number(selectedCourseId) },
    fetchPolicy: 'network-only',
    skip: !selectedCourseId,
  })

  const selectedCourse = selectedCourseId ? dataCourse?.courseById : null
  const lessons = useMemo(() => selectedCourse?.lessons || [], [selectedCourse])

  const [deleteCourse] = useMutation<IDeleteCourseResponse>(DELETE_COURSE)
  const [deleteLesson] = useMutation<IDeleteLessonResponse>(DELETE_LESSON)
  const [reorderLessons] = useMutation<IReorderLessonsResponse>(REORDER_LESSONS)

  const handleAddCourse = () => {
    setEditingCourse(null)
    onOpenCourse()
  }

  const handleEditCourse = (course: ICourse) => {
    setEditingCourse(course)
    onOpenCourse()
  }

  const handleDeleteCourse = async (course: ICourse) => {
    if (
      !window.confirm(intl.formatMessage(A.confirmDeleteCourse, {name: course.name}))
    )
      return
    try {
      const result = await deleteCourse({ variables: { id: parseId(course.id) } })
      if (result.data?.deleteCourse.success) {
        toast.success(intl.formatMessage(A.toastCourseDeleted))
        if (selectedCourseId === course.id) setSelectedCourseId(null)
        refetchCourses()
      } else if (result.data?.deleteCourse.errors?.length) {
        toast.error(intl.formatMessage(BACKEND_ERROR, {message: result.data.deleteCourse.errors[0].message}))
      }
    } catch (err) {
      console.error('Error deleting course:', err)
      toast.error(intl.formatMessage(A.errDeleteCourse))
    }
  }

  const handleAddLesson = () => {
    setEditingLesson(null)
    onOpenLesson()
  }

  const handleEditLesson = (lesson: ILesson) => {
    setEditingLesson(lesson)
    onOpenLesson()
  }

  const handleDeleteLesson = async (lesson: ILesson) => {
    if (!window.confirm(intl.formatMessage(A.confirmDeleteLesson, {name: lesson.displayTitle}))) return
    try {
      const result = await deleteLesson({ variables: { id: parseId(lesson.id) } })
      if (result.data?.deleteLesson.success) {
        toast.success(intl.formatMessage(A.toastLessonDeleted))
        refetchCourse()
        refetchCourses()
      } else if (result.data?.deleteLesson.errors?.length) {
        toast.error(intl.formatMessage(BACKEND_ERROR, {message: result.data.deleteLesson.errors[0].message}))
      }
    } catch (err) {
      console.error('Error deleting lesson:', err)
      toast.error(intl.formatMessage(A.errDeleteLesson))
    }
  }

  const handleMoveLesson = async (index: number, direction: -1 | 1) => {
    if (!selectedCourseId) return
    const targetIndex = index + direction
    if (targetIndex < 0 || targetIndex >= lessons.length) return

    const reordered = [...lessons]
    const [moved] = reordered.splice(index, 1)
    reordered.splice(targetIndex, 0, moved)

    try {
      const result = await reorderLessons({
        variables: {
          courseId: parseId(selectedCourseId),
          orderedLessonIds: reordered.map((lesson) => Number(parseId(lesson.id))),
        },
      })
      if (result.data?.reorderLessons.success) {
        refetchCourse()
      } else if (result.data?.reorderLessons.errors?.length) {
        toast.error(intl.formatMessage(BACKEND_ERROR, {message: result.data.reorderLessons.errors[0].message}))
      }
    } catch (err) {
      console.error('Error reordering lessons:', err)
      toast.error(intl.formatMessage(A.errReorderLessons))
    }
  }

  const handleOpenQuizBuilder = (lesson: ILesson) => {
    setQuizLesson(lesson)
    onOpenQuiz()
  }

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        {intl.formatMessage(A.errLoadingCourses, {message: error.message})}
      </div>
    )
  }

  return (
    <div className="container-fluid px-4 py-4">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <nav aria-label={intl.formatMessage(A.breadcrumb)}>
            <ol className="breadcrumb mb-1">
              <li className="breadcrumb-item">
                <Link to="/academy" className="text-decoration-none">
                  {intl.formatMessage(A.title)}
                </Link>
              </li>
              <li className="breadcrumb-item active">
                {intl.formatMessage(A.manageCourses)}
              </li>
            </ol>
          </nav>
          <h1 className="h3 mb-0">{intl.formatMessage(A.manageCourses)}</h1>
        </div>
        <button type="button" className="btn btn-primary btn-sm" onClick={handleAddCourse}>
          <i className="fa fa-plus me-1" /> {intl.formatMessage(A.newCourse)}
        </button>
      </div>

      <div className="row g-3">
        {/* Course list */}
        <div className="col-12 col-lg-5">
          <div className="card h-100">
            <div className="card-header py-3">
              <h6 className="mb-0">
                <i className="bi bi-mortarboard me-2" />
                {intl.formatMessage(A.coursesCount, {count: courses.length})}
              </h6>
            </div>
            <div className="list-group list-group-flush">
              {loadingCourses ? (
                <div className="list-group-item text-center py-4">
                  <div className="spinner-border spinner-border-sm text-primary" role="status">
                    <span className="visually-hidden">{intl.formatMessage(A.loading)}</span>
                  </div>
                </div>
              ) : courses.length === 0 ? (
                <div className="list-group-item text-center text-muted py-4">
                  {intl.formatMessage(A.noCoursesYet)}
                </div>
              ) : (
                courses.map((course) => (
                  <div
                    key={course.id}
                    className={`list-group-item list-group-item-action d-flex align-items-center gap-2 ${
                      selectedCourseId === course.id ? 'active' : ''
                    }`}
                    style={{ cursor: 'pointer' }}
                    onClick={() => setSelectedCourseId(course.id)}
                  >
                    <div className="flex-fill">
                      <div className="fw-semibold d-flex align-items-center gap-2">
                        {course.name}
                        {!course.isActive && (
                          <span className="badge bg-warning text-dark">
                    {intl.formatMessage(A.draft)}
                  </span>
                        )}
                      </div>
                      <small className={selectedCourseId === course.id ? '' : 'text-muted'}>
                        {intl.formatMessage(A.lessonCount, {count: course.lessonsCount})}
                      </small>
                    </div>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-warning"
                      title={intl.formatMessage(A.editCourse)}
                      onClick={(e) => {
                        e.stopPropagation()
                        handleEditCourse(course)
                      }}
                    >
                      <i className="bi bi-pencil" />
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger"
                      title={intl.formatMessage(A.deleteCourse)}
                      onClick={(e) => {
                        e.stopPropagation()
                        handleDeleteCourse(course)
                      }}
                    >
                      <i className="bi bi-trash" />
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Lessons of selected course */}
        <div className="col-12 col-lg-7">
          <div className="card h-100">
            <div className="card-header d-flex justify-content-between align-items-center py-3">
              <h6 className="mb-0">
                <i className="bi bi-collection-play me-2" />
                {selectedCourse
                  ? intl.formatMessage(A.lessonsOf, {name: selectedCourse.name})
                  : intl.formatMessage(A.lessons)}
              </h6>
              {selectedCourse && (
                <button type="button" className="btn btn-sm btn-primary" onClick={handleAddLesson}>
                  <i className="fa fa-plus me-1" /> {intl.formatMessage(A.addLesson)}
                </button>
              )}
            </div>
            <div className="list-group list-group-flush">
              {!selectedCourseId ? (
                <div className="list-group-item text-center text-muted py-5">
                  <i className="bi bi-arrow-left-circle d-block mb-2" style={{ fontSize: 32 }} />
                  {intl.formatMessage(A.pickCourse)}
                </div>
              ) : loadingCourse ? (
                <div className="list-group-item text-center py-4">
                  <div className="spinner-border spinner-border-sm text-primary" role="status">
                    <span className="visually-hidden">{intl.formatMessage(A.loading)}</span>
                  </div>
                </div>
              ) : lessons.length === 0 ? (
                <div className="list-group-item text-center text-muted py-4">
                  {intl.formatMessage(A.noLessonsYet)}
                </div>
              ) : (
                lessons.map((lesson, idx) => (
                  <div key={lesson.id} className="list-group-item d-flex align-items-center gap-2">
                    {/* Reorder arrows */}
                    <div className="d-flex flex-column">
                      <button
                        type="button"
                        className="btn btn-sm p-0 border-0 text-muted"
                        disabled={idx === 0}
                        onClick={() => handleMoveLesson(idx, -1)}
                        title={intl.formatMessage(A.moveUp)}
                      >
                        <i className="bi bi-chevron-up" />
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm p-0 border-0 text-muted"
                        disabled={idx === lessons.length - 1}
                        onClick={() => handleMoveLesson(idx, 1)}
                        title={intl.formatMessage(A.moveDown)}
                      >
                        <i className="bi bi-chevron-down" />
                      </button>
                    </div>

                    <span className="text-muted">
                      {idx + 1}
                      {ORDINAL_DOT}
                    </span>
                    <span className="me-1">{getResourceTypeIcon(lesson.resource.resourceType)}</span>
                    <div className="flex-fill">
                      <div className="fw-semibold">{lesson.displayTitle}</div>
                      <small className="text-muted">
                        {lesson.resource.name}
                        {!lesson.isRequired && intl.formatMessage(A.optionalSuffix)}
                        {lesson.hasQuiz && (
                          <span className="ms-1">
                            {DOT_SEP}
                            <i className="bi bi-patch-question" /> {intl.formatMessage(A.quizSuffix)}
                          </span>
                        )}
                      </small>
                    </div>

                    <button
                      type="button"
                      className={`btn btn-sm ${lesson.hasQuiz ? 'btn-outline-primary' : 'btn-outline-secondary'}`}
                      title={intl.formatMessage(lesson.hasQuiz ? A.editQuiz : A.addQuiz)}
                      onClick={() => handleOpenQuizBuilder(lesson)}
                    >
                      <i className="bi bi-patch-question" />
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-warning"
                      title={intl.formatMessage(A.editLesson)}
                      onClick={() => handleEditLesson(lesson)}
                    >
                      <i className="bi bi-pencil" />
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger"
                      title={intl.formatMessage(A.deleteLesson)}
                      onClick={() => handleDeleteLesson(lesson)}
                    >
                      <i className="bi bi-trash" />
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      {isOpenCourse && (
        <ModalCourseForm
          isOpen={isOpenCourse}
          onClose={onCloseCourse}
          refetch={() => {
            refetchCourses()
            if (selectedCourseId) refetchCourse()
          }}
          course={editingCourse}
        />
      )}

      {isOpenLesson && selectedCourseId && (
        <ModalLessonForm
          isOpen={isOpenLesson}
          onClose={onCloseLesson}
          refetch={() => {
            refetchCourse()
            refetchCourses()
          }}
          courseId={selectedCourseId}
          lesson={editingLesson}
        />
      )}

      {isOpenQuiz && quizLesson && (
        <QuizBuilderModal
          isOpen={isOpenQuiz}
          onClose={onCloseQuiz}
          lesson={quizLesson}
          refetchCourse={refetchCourse}
        />
      )}
    </div>
  )
}
